import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import mtcDailyMonitoringApi from "../../../api/mtc/mtcDailyMonitoringApi";
import type {
  MtcDailyMonitoringData,
  MtcDailyMonitoringPayload,
} from "../../../api/mtc/types/mtcDailyMonitoringType";
import {
  isRequestCancelled,
  type RejectPayload,
  toRejectPayload,
} from "../../../helpers/ApiError";
import type { RootState } from "../../store";

interface MtcDailyMonitoringState {
  data: MtcDailyMonitoringData | null;
  isLoading: boolean;
  error: string | null;
  requestId: string | null;
}

const initialState: MtcDailyMonitoringState = {
  data: null,
  isLoading: false,
  error: null,
  requestId: null,
};

export const fetchMtcDailyMonitoring = createAsyncThunk<
  MtcDailyMonitoringData,
  MtcDailyMonitoringPayload,
  { rejectValue: RejectPayload }
>(
  "mtcDailyMonitoring/fetchMtcDailyMonitoring",
  async (payload, { rejectWithValue, signal }) => {
    try {
      const response = await mtcDailyMonitoringApi.getDailyMonitoring(
        payload,
        signal,
      );

      return response.data;
    } catch (err) {
      if (isRequestCancelled(err, signal)) {
        throw err;
      }

      return rejectWithValue(
        toRejectPayload(err, "Gagal mengambil data daily monitoring"),
      );
    }
  },
);

const mtcDailyMonitoringSlice = createSlice({
  name: "mtcDailyMonitoring",
  initialState,
  reducers: {
    setMtcDailyMonitoring: (
      state,
      action: PayloadAction<MtcDailyMonitoringData>,
    ) => {
      state.data = action.payload;
      state.error = null;
    },

    clearMtcDailyMonitoring: (state) => {
      state.data = null;
      state.error = null;
      state.requestId = null;
      state.isLoading = false;
    },

    clearMtcDailyMonitoringError: (state) => {
      state.error = null;
    },

    resetMtcDailyMonitoring: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchMtcDailyMonitoring.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })

      .addCase(fetchMtcDailyMonitoring.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) {
          return;
        }

        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
        state.requestId = null;
      })

      .addCase(fetchMtcDailyMonitoring.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) {
          return;
        }

        state.isLoading = false;
        state.requestId = null;

        if (action.meta.aborted) {
          state.error = null;
          return;
        }

        state.error =
          action.payload?.message ?? "Gagal mengambil data daily monitoring";
      });
  },
});

export const {
  setMtcDailyMonitoring,
  clearMtcDailyMonitoring,
  clearMtcDailyMonitoringError,
  resetMtcDailyMonitoring,
} = mtcDailyMonitoringSlice.actions;

export const selectMtcDailyMonitoring = (state: RootState) =>
  state.mtcDailyMonitoringReducer.data;

export const selectIsLoadingMtcDailyMonitoring = (state: RootState) =>
  state.mtcDailyMonitoringReducer.isLoading;

export const selectMtcDailyMonitoringError = (state: RootState) =>
  state.mtcDailyMonitoringReducer.error;

export default mtcDailyMonitoringSlice.reducer;
